import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { AdminTariffService, USER_ROLE } from '@/shared/api'
import type { PreviewResponse, TariffDetailsDTO, TariffVersionDTO } from '@/shared/api'
import { useTariff, useTariffMutation, useUsers, useUserDetails, useDebounce } from '@/shared/hooks'
import { Button, Input, PageHeader } from '@/shared/ui'
import { TariffEditor } from '@/shared/ui/TariffEditor'

const SCOPE_LABELS: Record<number, string> = { 1: 'Общий', 2: 'Индивидуальный' }

interface PreviewForm {
  courier_id: string
  payment_type_name: string
  priority: string
  items_count: string
  distance_km: string
}

const EMPTY_PREVIEW: PreviewForm = {
  courier_id: '',
  payment_type_name: '',
  priority: '0',
  items_count: '1',
  distance_km: '',
}

export const TariffCardPage: React.FC = () => {
  const { tariffId } = useParams({ from: '/tariffs/$tariffId' })
  const id = Number(tariffId)
  const navigate = useNavigate()
  const { data, isLoading } = useTariff(id)
  const [draft, setDraft] = useState<TariffDetailsDTO | null>(null)
  const [previewForm, setPreviewForm] = useState<PreviewForm>(EMPTY_PREVIEW)
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [courierSearch, setCourierSearch] = useState('')

  useEffect(() => {
    if (data) setDraft(data)
  }, [data])

  const saveDraft = useTariffMutation(
    (v: TariffVersionDTO) =>
      AdminTariffService.updateVersion(id, v.courier_tariff_version_id, v),
    'Черновик сохранён'
  )
  const publish = useTariffMutation(
    (v: TariffVersionDTO) =>
      AdminTariffService.publishVersion(id, v.courier_tariff_version_id),
    'Версия опубликована'
  )
  const createVersion = useTariffMutation(
    () => AdminTariffService.createVersion(id),
    'Создана версия'
  )
  const setActive = useTariffMutation(
    (active: boolean) => AdminTariffService.setActive(id, active),
    'Статус обновлён'
  )
  const duplicate = useTariffMutation<void>(
    () => AdminTariffService.duplicate(id),
    'Тариф продублирован'
  )
  const setCourier = useTariffMutation<number>(
    (courier: number) => AdminTariffService.setCourier(id, courier),
    'Курьер изменён'
  )
  const removeTariff = useTariffMutation<void>(
    () => AdminTariffService.remove(id),
    'Тариф удалён'
  )

  // Поиск курьеров для назначения индивидуального тарифа (как в «Все курьеры»).
  const debouncedCourierSearch = useDebounce(courierSearch, 400)
  const { data: courierResults, isLoading: couriersLoading } = useUsers(
    debouncedCourierSearch.trim()
      ? {
          search: debouncedCourierSearch.trim(),
          role: USER_ROLE.EXTERNAL_COURIER,
          page: 1,
          limit: 20,
        }
      : undefined
  )
  const currentCourierId = data?.tariff.courier_id ?? 0
  const { data: currentCourier } = useUserDetails(currentCourierId, {
    enabled: !!currentCourierId,
  })

  if (isLoading || !draft) return <div className="p-6">Загрузка…</div>

  // По умолчанию — режим просмотра (последняя опубликованная версия).
  // Правка — только после кнопки «Редактировать» (черновик).
  const viewVersion = draft.versions.find((v) => v.status === 1) ?? draft.versions[0]
  const draftVersion = draft.versions.find((v) => v.status === 0)
  const shownVersion = isEditing && draftVersion ? draftVersion : viewVersion
  // Правка возможна только при наличии черновика (после публикации — снова просмотр).
  const editing = isEditing && !!draftVersion
  if (!shownVersion) return <div className="p-6">Версии не найдены</div>

  const handleChange = (version: TariffVersionDTO) => {
    setDraft({
      ...draft,
      versions: draft.versions.map((v) =>
        v.courier_tariff_version_id === version.courier_tariff_version_id ? version : v
      ),
    })
  }

  const handleDuplicate = async () => {
    const created = (await duplicate.mutateAsync(undefined)) as TariffDetailsDTO
    navigate({
      to: '/tariffs/$tariffId',
      params: { tariffId: String(created.tariff.courier_tariff_id) },
    })
  }

  const handleDelete = async () => {
    if (!window.confirm('Удалить тариф? Действие необратимо.')) return
    await removeTariff.mutateAsync(undefined)
    navigate({ to: '/tariffs' })
  }

  const handlePreview = async () => {
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      const result = await AdminTariffService.preview({
        courier_id: Number(previewForm.courier_id),
        payment_type_name: previewForm.payment_type_name,
        priority: Number(previewForm.priority),
        items_count: Number(previewForm.items_count),
        distance_km:
          previewForm.distance_km === '' ? null : Number(previewForm.distance_km),
      })
      setPreview(result)
    } catch (e) {
      setPreview(null)
      setPreviewError(e instanceof Error ? e.message : 'Ошибка расчёта')
    } finally {
      setPreviewLoading(false)
    }
  }

  const isActive = draft.tariff.status === 1

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title={draft.tariff.name}
        description={
          draft.tariff.description ??
          `Область: ${SCOPE_LABELS[draft.tariff.scope] ?? '—'}${
            draft.tariff.courier_id ? `, курьер #${draft.tariff.courier_id}` : ''
          }`
        }
      >
        {/* Возврат к списку тарифов: карточка открывается из него, и без кнопки
            единственный путь назад — браузерная «назад». */}
        <Link to="/tariffs">
          <Button variant="secondary" size="sm">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Назад
          </Button>
        </Link>
        <Button
          variant="primary"
          onClick={() => {
            // Вход в режим правки: при отсутствии черновика создаём его.
            if (!draftVersion) createVersion.mutate(undefined)
            setIsEditing(true)
          }}
          isLoading={createVersion.isPending}
        >
          Редактировать
        </Button>
        {draft.tariff.scope === 2 && (
          <>
            <Button
              variant="secondary"
              onClick={handleDuplicate}
              isLoading={duplicate.isPending}
            >
              Дублировать
            </Button>
            <Button
              variant="secondary"
              onClick={() => setActive.mutate(!isActive)}
              isLoading={setActive.isPending}
            >
              {isActive ? 'Деактивировать' : 'Активировать'}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={removeTariff.isPending}
            >
              Удалить
            </Button>
          </>
        )}
      </PageHeader>

      <div className="p-6 space-y-4 overflow-auto">
        {draft.tariff.scope === 2 && (
          <div className="rounded border border-gray-200 p-4 space-y-3">
            <div className="font-medium">
              Курьер:{' '}
              {currentCourier?.full_name
                ? `${currentCourier.full_name} (#${draft.tariff.courier_id})`
                : draft.tariff.courier_id
                  ? `#${draft.tariff.courier_id}`
                  : '— не задан'}
            </div>
            <div className="space-y-2">
              <Input
                placeholder="Поиск курьера по имени, телефону, email..."
                value={courierSearch}
                onChange={(e) => setCourierSearch(e.target.value)}
              />
              {courierSearch.trim().length > 0 && (
                <div className="max-h-56 overflow-auto rounded border border-gray-200">
                  {couriersLoading && (
                    <div className="px-3 py-2 text-sm text-gray-500">Поиск…</div>
                  )}
                  {!couriersLoading && (courierResults?.items ?? []).length === 0 && (
                    <div className="px-3 py-2 text-sm text-gray-500">Не найдено</div>
                  )}
                  {(courierResults?.items ?? []).map((u) => (
                    <button
                      key={u.user_id}
                      type="button"
                      className="block w-full px-3 py-2 text-left hover:bg-gray-50"
                      onClick={() => {
                        setCourier.mutate(u.user_id)
                        setCourierSearch('')
                      }}
                    >
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-sm text-gray-500">
                        #{u.user_id} · {u.phone || '—'}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {setCourier.isPending && (
                <div className="text-sm text-gray-500">Сохранение…</div>
              )}
            </div>
          </div>
        )}
        <TariffEditor version={shownVersion} onChange={handleChange} readonly={!editing} />

        {editing && draftVersion && (
          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={() => saveDraft.mutate(draftVersion)}
              isLoading={saveDraft.isPending}
            >
              Сохранить черновик
            </Button>
            <Button
              variant="secondary"
              onClick={() => publish.mutate(draftVersion)}
              isLoading={publish.isPending}
            >
              Опубликовать
            </Button>
          </div>
        )}

        <div className="rounded border border-gray-200 p-4 space-y-3">
          <div className="font-medium">Предпросмотр</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="ID курьера"
              type="number"
              value={previewForm.courier_id}
              onChange={(e) =>
                setPreviewForm((p) => ({ ...p, courier_id: e.target.value }))
              }
            />
            <Input
              label="Тип оплаты"
              value={previewForm.payment_type_name}
              onChange={(e) =>
                setPreviewForm((p) => ({ ...p, payment_type_name: e.target.value }))
              }
            />
            <Input
              label="Приоритет"
              type="number"
              value={previewForm.priority}
              onChange={(e) =>
                setPreviewForm((p) => ({ ...p, priority: e.target.value }))
              }
            />
            <Input
              label="Количество мест"
              type="number"
              value={previewForm.items_count}
              onChange={(e) =>
                setPreviewForm((p) => ({ ...p, items_count: e.target.value }))
              }
            />
            <Input
              label="Расстояние, км"
              type="number"
              min={0}
              step="0.1"
              value={previewForm.distance_km}
              onChange={(e) =>
                setPreviewForm((p) => ({ ...p, distance_km: e.target.value }))
              }
            />
          </div>
          <Button onClick={handlePreview} isLoading={previewLoading}>
            Рассчитать
          </Button>
          {previewError && <div className="text-sm text-error-600">{previewError}</div>}
          {preview && (
            <div className="space-y-1 text-sm">
              <div>Итого: <span className="font-medium">{preview.total} ₽</span></div>
              <div>Базовая ставка: {preview.base_rate} ₽</div>
              {preview.distance_surcharge > 0 && (
                <div>
                  Расстояние {preview.distance_km} км: +{preview.distance_surcharge} ₽
                </div>
              )}
              <div>
                Прочие надбавки:{' '}
                {Math.round((preview.surcharges_total - preview.distance_surcharge) * 100) / 100} ₽
              </div>
              <div>
                Коэффициент: {preview.coefficient}
                {preview.priority !== null ? ` (приоритет ${preview.priority})` : ''}
              </div>
              {preview.chosen_coefficient_id !== null && (
                <div>Выбран коэффициент #{preview.chosen_coefficient_id}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
